import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import moment from 'moment';

import { formatNumber, valueExists } from '../../../common/utils/utils';
import { getLocalTime } from '../../../common/utils/time/time';
import { getProductImageUrl } from '../../../common/utils/products/productImageUrlResolver';
import { formatExpiryBatchesText } from '../../../common/utils/warehousing/formatExpiryBatchSummary';
import { ProductImage } from '../../components/image';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { Dialog } from '../../components';
import { PrimaryButton, SecondaryButton } from '../../design/components';
import { DateSelectorInput } from '../../components/input';
import Text from '../../components/text/Text';
import TextInput from '../../components/input/TextInput';
import StocktakeQuantitySelector from '../../components/stocktake/StocktakeQuantitySelector';

import { formatQuantityWithPackaging, resolveDefaultExpiryDateForNewStocktakeBatchRow } from './StocktakeDetailHelpers';
import { createStocktakeDetailStyles } from './StocktakeDetail.styles';

const StocktakeBatchDialog = ({
	item,
	barcode,
	rows,
	setRows,
	currentWarehouseRows,
	error,
	onClose,
	onSubmit,
	loading,
	loadingWarehouseRows,
	canEdit,
	timezone,
}) => {
	const styles = useThemedStyles(createStocktakeDetailStyles);
	const imageUri = item?.warehouseProduct?.product ? getProductImageUrl(item.warehouseProduct.product) : null;
	const rowsTotal = useMemo(() => {
		return (rows || []).reduce((acc, row) => acc + Math.max(0, Math.round(Number(row.quantity) || 0)), 0);
	}, [rows]);
	const hasQuarantinedExpiryDate = useMemo(() => {
		const currentDateKey = getLocalTime(timezone).format('YYYY-MM-DD');
		return (rows || []).some(row => {
			const expiryDate = moment(row?.expiryDate, 'YYYY-MM-DD', true);
			return expiryDate.isValid() &&
				!expiryDate.isAfter(moment(currentDateKey, 'YYYY-MM-DD', true), 'day');
		});
	}, [rows, timezone]);

	const currentWarehouseSummary = useMemo(() => {
		return formatExpiryBatchesText(currentWarehouseRows || [], {
			fallback: 'n/a',
			maxRows: 3,
			timezone,
		});
	}, [currentWarehouseRows, timezone]);

	const addRow = useCallback(() => {
		const defaultExpiryDate = resolveDefaultExpiryDateForNewStocktakeBatchRow({
			rows,
			currentWarehouseRows,
		});

		setRows(currentRows => ([
			...(currentRows || []),
			{ quantity: null, expiryDate: defaultExpiryDate, supplierBatchCode: '' },
		]));
	}, [currentWarehouseRows, rows, setRows]);

	const removeRow = useCallback((index) => {
		setRows(currentRows => currentRows.filter((_, rowIndex) => rowIndex !== index));
	}, [setRows]);

	const updateRow = useCallback((index, field, value) => {
		setRows(currentRows => currentRows.map((row, rowIndex) => {
			if (rowIndex !== index) {
				return row;
			}

			return {
				...row,
				[field]: value,
			};
		}));
	}, [setRows]);

	const handleSubmit = useCallback(() => {
		onSubmit(item, rows || []);
	}, [item, onSubmit, rows]);

	return (
		<Dialog
			isOpen={Boolean(item)}
			onClose={onClose}
			shouldShowCloseIcon={true}
			enableCloseDialog={!loading}
		>
			{item && (
				<View style={styles.batchDialogContainer}>
					<KeyboardAwareScrollView
						style={styles.batchDialogScroll}
						contentContainerStyle={styles.batchDialogContent}
						enableOnAndroid={true}
						extraScrollHeight={32}
						keyboardShouldPersistTaps="handled"
						showsVerticalScrollIndicator={false}
					>
						<Text style={styles.dialogTitle}>
							{item.warehouseProduct?.product?.name || 'Product'}
						</Text>
						{imageUri && (
							<ProductImage
								uri={imageUri}
								style={styles.dialogProductImage}
							/>
						)}
						{barcode && (
							<Text style={styles.dialogMeta}>
								Scanned barcode: {barcode}
							</Text>
						)}
						<Text style={styles.dialogMeta}>
							Pre stocktake quantity: {valueExists(item.preStocktakeQuantity)
								? formatQuantityWithPackaging(item.preStocktakeQuantity, item.warehouseProduct)
								: '—'}
						</Text>
						<Text style={styles.dialogMeta}>
							Current warehouse batches: {currentWarehouseSummary}
						</Text>
						<Text style={styles.dialogMeta}>
							Record the expiry dates physically present in the warehouse.
						</Text>
						{hasQuarantinedExpiryDate && (
							<Text style={styles.warningText}>
								Stock expiring today or earlier will be quarantined and cannot be picked or transferred; remove or spoil it, or correct the date.
							</Text>
						)}
						{loadingWarehouseRows && (
							<Text style={styles.dialogMeta}>Refreshing current batches…</Text>
						)}
						{(rows || []).map((row, index) => (
							<View key={`${item.id}_${index}`} style={styles.batchRow}>
								<Text style={styles.batchHeading}>Batch {index + 1}</Text>
								<StocktakeQuantitySelector
									key={`${item.id}_${index}`}
									warehouseProduct={item.warehouseProduct}
									value={valueExists(row.quantity) ? row.quantity : null}
									onChange={value => updateRow(index, 'quantity', value)}
									disabled={!canEdit || loading}
								/>
								<DateSelectorInput
									value={row.expiryDate || ''}
									onChange={value => updateRow(index, 'expiryDate', value)}
									placeholder="Expiry date (optional)"
									disabled={!canEdit || loading}
									style={styles.batchInput}
								/>
								<TextInput
									value={row.supplierBatchCode || ''}
									onChange={value => updateRow(index, 'supplierBatchCode', value)}
									placeholder="Supplier batch code (optional)"
									style={styles.batchInput}
								/>
								{rows.length > 1 && (
									<SecondaryButton
										style={styles.batchInput}
										icon="trash-2"
										label="Remove batch"
										onPress={() => removeRow(index)}
										disabled={!canEdit || loading}
									/>
								)}
							</View>
						))}
						<View style={styles.batchFooter}>
							<SecondaryButton
								icon="plus"
								label="Add batch"
								onPress={addRow}
								disabled={!canEdit || loading}
							/>
							<Text style={styles.batchTotalText}>
								Total entered: {formatNumber(rowsTotal)} units
							</Text>
						</View>
						{Boolean(error) && <Text style={styles.errorText}>{error}</Text>}
					</KeyboardAwareScrollView>
					<View style={styles.dialogButtonRow}>
						<SecondaryButton
							style={styles.halfButton}
							label="Cancel"
							onPress={onClose}
							disabled={loading}
						/>
						<PrimaryButton
							style={styles.halfButton}
							size="compact"
							label="Save batches"
							onPress={handleSubmit}
							loading={loading}
							disabled={!canEdit || loading || loadingWarehouseRows}
						/>
					</View>
				</View>
			)}
		</Dialog>
	);
};

export default StocktakeBatchDialog;
